import { useState, useEffect } from "react";
import { useAdmin } from "../../../contexts/AdminContext";

function useCountdown(targetDateStr: string) {
  const calc = () => {
    const DRAW_DATE = new Date(targetDateStr + "T20:00:00");
    const diff = DRAW_DATE.getTime() - Date.now();
    if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    return {
      days: Math.floor(diff / 86400000),
      hours: Math.floor((diff / 3600000) % 24),
      minutes: Math.floor((diff / 60000) % 60),
      seconds: Math.floor((diff / 1000) % 60),
    };
  };
  const [time, setTime] = useState(calc());
  useEffect(() => {
    const iv = setInterval(() => setTime(calc()), 1000);
    return () => clearInterval(iv);
  }, [targetDateStr]);
  return time;
}

interface BlockProps {
  value: number;
  label: string;
  urgent?: boolean;
}

function CountBlock({ value, label, urgent }: BlockProps) {
  const display = String(value).padStart(2, "0");

  return (
    <div className="flex flex-col items-center">
      <div
        className={`relative w-12 h-12 sm:w-16 sm:h-16 rounded-lg sm:rounded-xl flex items-center justify-center overflow-hidden transition-all duration-300 ${
          urgent
            ? "bg-red-600 border border-red-400 shadow-inner"
            : "bg-slate-800 border border-slate-700 shadow-inner"
        }`}
      >
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: "linear-gradient(135deg, rgba(255,255,255,0.12) 0%, transparent 55%)",
          }}
        />
        <div className="absolute inset-x-0 top-1/2 -translate-y-px h-px bg-white/10 pointer-events-none" />
        
        {/* Simple fade-in animation on value change */}
        <span
          key={display}
          className={`font-mono font-bold text-white text-xl sm:text-3xl leading-none animate-in fade-in zoom-in duration-300 ${urgent ? "text-red-200" : ""}`}
        >
          {display}
        </span>
      </div>
      <span
        className={`text-[9px] sm:text-xs uppercase tracking-widest mt-1.5 font-bold ${
          urgent ? "text-red-300/80" : "text-white/50"
        }`}
      >
        {label}
      </span>
    </div>
  );
}

export default function HeroCountdown() {
  const { settings } = useAdmin();
  const { days, hours, minutes, seconds } = useCountdown(settings.drawClosingDate);
  const urgent = days < 3;

  return (
    <div className="w-full max-w-lg mx-auto mt-6">
      <div
        className={`rounded-2xl px-5 py-4 border shadow-[0_20px_50px_rgba(0,0,0,0.3)] ${
          urgent
            ? "bg-red-950 border-red-800"
            : "bg-slate-900 border-slate-800"
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-center gap-2 mb-4">
          <span
            className={`w-1.5 h-1.5 rounded-full animate-pulse inline-block ${
              urgent ? "bg-red-400" : "bg-primary-light"
            }`}
          />
          <span
            className={`text-xs font-semibold uppercase tracking-widest ${
              urgent ? "text-red-300" : "text-primary-light"
            }`}
          >
            {urgent ? "Letzte Chance!" : "Teilnahmeschluss Verlosung #52"}
          </span>
          <span
            className={`w-1.5 h-1.5 rounded-full animate-pulse inline-block ${
              urgent ? "bg-red-400" : "bg-primary-light"
            }`}
          />
        </div>

        {/* Blocks */}
        <div className="flex items-end justify-center gap-1.5 sm:gap-3">
          <CountBlock value={days} label="Tage" urgent={urgent} />

          <span className="text-white/25 font-bold text-xl sm:text-3xl leading-none mb-4 sm:mb-5">:</span>

          <CountBlock value={hours} label="Std." urgent={urgent} />

          <span className="text-white/25 font-bold text-xl sm:text-3xl leading-none mb-4 sm:mb-5">:</span>

          <CountBlock value={minutes} label="Min." urgent={urgent} />

          <span className="text-white/25 font-bold text-xl sm:text-3xl leading-none mb-4 sm:mb-5">:</span>

          <CountBlock value={seconds} label="Sek." urgent={urgent && days === 0} />
        </div>

        {/* Footer note */}
        <p className="text-center text-white/40 text-xs mt-4">
          {new Date(settings.drawClosingDate).toLocaleDateString('de-DE', { day: 'numeric', month: 'long', year: 'numeric' })} um 20:00 Uhr · Zertifizierte Ziehung durch {settings.bailiffName?.split(' ')[0] || "Notar"}
        </p>
      </div>
    </div>
  );
}
